import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { supportsWebGL } from '@/lib/webgl'

interface ProductImageProps {
    src: string | null
    alt: string
    designKey: string | null
    mainColor?: string | null
    accentColor?: string | null
    className?: string
    imageClassName?: string
    priority?: boolean
    backdrop?: 'studio' | 'none'
}

// Shows the product photo when there is one, otherwise a studio render of the doll's 3D model.
// A product with neither shows a quiet placeholder, never a stand-in model.
export const ProductImage: FC<ProductImageProps> = ({
    src,
    alt,
    designKey,
    mainColor = null,
    accentColor = null,
    className,
    imageClassName,
    priority = false,
    backdrop = 'studio'
}): ReactNode => {
    const wrapper = useRef<HTMLDivElement>(null)
    const [render, setRender] = useState<string | null>(null)
    const [shownRender, setShownRender] = useState<string | null>(null)

    useEffect(() => {
        if (src || !designKey) return
        if (!supportsWebGL()) return

        let cancelled = false
        const node = wrapper.current

        const start = () => {
            import('@/components/three/doll-snapshot')
                .then(module => module.renderDollSnapshot({ designKey, main: mainColor, accent: accentColor }))
                .then(url => {
                    if (!cancelled && url) setRender(url)
                })
                .catch(() => undefined)
        }

        if (!node || !('IntersectionObserver' in window)) {
            start()

            return () => {
                cancelled = true
            }
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                observer.disconnect()
                start()
            }
        }, { rootMargin: '300px' })

        observer.observe(node)

        return () => {
            cancelled = true
            observer.disconnect()
        }
    }, [src, designKey, mainColor, accentColor])

    const source = src ?? render

    return (
        <div
            ref={wrapper}
            className={cn(
                'relative overflow-hidden',
                backdrop === 'studio' && 'bg-[radial-gradient(120%_90%_at_50%_10%,#fffaf3_0%,#f8ebe0_55%,#f1dccf_100%)]',
                className
            )}
        >
            {!source && !designKey && (
                <div role={alt ? 'img' : undefined} aria-label={alt || undefined} aria-hidden={alt ? undefined : true} className='absolute inset-0 flex flex-col items-center justify-center gap-2 text-center'>
                    <svg viewBox='0 0 64 64' aria-hidden='true' className='size-12 text-blush-300'>
                        <circle cx='32' cy='32' r='22' fill='currentColor' opacity='0.45' />
                        <g fill='none' stroke='#fffaf3' strokeWidth='2.4' strokeLinecap='round'>
                            <path d='M14 27c10-6 26-6 36 0' />
                            <path d='M12 35c12-7 28-7 40 0' />
                            <path d='M26 11c-6 10-6 32 0 42' />
                            <path d='M38 11c6 10 6 32 0 42' />
                        </g>
                    </svg>
                    <span className='px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-cocoa-500/80'>
                        Foto segera hadir
                    </span>
                </div>
            )}

            {!source && designKey && (
                <div className='absolute inset-0 flex items-center justify-center' aria-hidden='true'>
                    <svg viewBox='0 0 64 64' className='size-14 animate-pulse text-blush-300'>
                        <circle cx='32' cy='32' r='22' fill='currentColor' opacity='0.45' />
                        <g fill='none' stroke='#fffaf3' strokeWidth='2.4' strokeLinecap='round'>
                            <path d='M14 27c10-6 26-6 36 0' />
                            <path d='M12 35c12-7 28-7 40 0' />
                            <path d='M26 11c-6 10-6 32 0 42' />
                            <path d='M38 11c6 10 6 32 0 42' />
                        </g>
                    </svg>
                </div>
            )}

            {src && (
                <img
                    src={src}
                    alt={alt}
                    loading={priority ? 'eager' : 'lazy'}
                    decoding='async'
                    className={cn('absolute inset-0 h-full w-full object-cover', imageClassName)}
                />
            )}

            {!src && render && (
                <img
                    src={render}
                    alt={alt}
                    decoding='async'
                    onLoad={() => setShownRender(render)}
                    className={cn(
                        'absolute inset-0 h-full w-full object-contain p-[5%] transition-opacity duration-500',
                        shownRender === render ? 'opacity-100' : 'opacity-0',
                        imageClassName
                    )}
                />
            )}
        </div>
    )
}
